import {
  addTagToDeviceEndPoint,
  fetchDiscoveredCamerasEndPointByCompanion,
  fetchVisionDevicesEndPoint,
  forceDiscoveryAPI,
  fetchCompanionDeviceEndPoint,
  updateCameraCredentialsOnCCD,
  fetchCameraAndFovsForRbacEndPoint,
  updateonboardedEndPoint,
  getCameraCredsEndPoint
} from "../configuration";
import { DEVICE_CONSTANTS } from "../constants/DeviceConstants";
import axiosInstance from "../global/axiosInterceptor";
import { getResourceIdentifierForDevice, handleApiError, encodeOrDecodeRtspCredentials } from "../global/utility/Utils";

export const PatchDeviceInfo = async (site, floor, sendVideo, companionDeviceName, activeSession, deviceInfo) => {
  try {
    let resourceidentifier = {};
    let refidType = deviceInfo[0]?.refidtype;
    let id = deviceInfo[0]?.refid;

    if (refidType === "uuid") {
      refidType = "deviceid";
      id = deviceInfo[0]?.id;
    }

    resourceidentifier[refidType] = id;

    const cansendmedia = sendVideo === true ? true : false;

    let device = {
      tagids: [site],
      customdata: {
        cansendmedia: cansendmedia
      },
      name: companionDeviceName
    };

    if (floor && floor.trim() !== "") {
      device.tagids.push(floor);
    }

    const requestBody = {
      accountname: activeSession.accountId,
      resourceidentifier: resourceidentifier,
      device: device
    };

    const response = await axiosInstance.patch(addTagToDeviceEndPoint(), requestBody);

    const res = response.data;
    if (response.status === 200) {
      console.log("Tag added to device");
      return {
        success: true,
        message: "Added details successfully!",
        response: response.data
      };
    } else {
      console.log("Failed to add tag to device");
      throw new Error({
        success: false,
        message: "Failed to add details, please try again"
      });
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to add device details", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to add device details, please try again");
    return [];
  }
};

export const updateCameraCredentials = async (deviceInfo, username, password, activeSession) => {
  try {
    let resourceidentifier = {};
    let refidType = deviceInfo[0]?.refidtype;
    let id = deviceInfo[0]?.refid;

    if (refidType === "uuid") {
      refidType = "deviceid";
      id = deviceInfo[0]?.id;
    }

    resourceidentifier[refidType] = id;

    const requestBody = {
      accountname: activeSession.accountId,
      resourceidentifier: resourceidentifier,
      sensorsauth: {
        username,
        password
      }
    };

    const response = await axiosInstance.post(updateCameraCredentialsOnCCD(), requestBody);

    if (response.status === 204 || response.status === 200) {
      console.log("Added credentials successfully");
      return {
        success: true,
        message: "Added credentials successfully!",
        response: response.data
      };
    } else {
      console.log("Failed to update credentials");
      return {
        success: false,
        message: "failed to add credentials"
      };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to update camera credentials", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to update camera credentials, please reload the page.");
    return [];
  }
};

export const updateOnboardedDetails = async (payload, activeSession, modalFlow = false) => {
  try {
    let resourceidentifier = {};
    resourceidentifier["qrcode"] = payload?.companionQrcode;
    const requestBody = {
      accountname: activeSession.accountId,
      resourceidentifier: {
        qrcode: payload?.companionQrcode
      },
      cameras: [
        {
          username: encodeURIComponent(payload?.userName),
          password: encodeURIComponent(payload?.pass),
          qrcode: payload?.cameraDetailsId,
          fovs: payload?.fovData.map((item) => ({
            fovid: item?.id ? item?.id : item?.fovId,
            encoders:
              modalFlow === false
                ? item?.customdata.encoders.map((enc) => ({
                    ...enc,
                    url: encodeOrDecodeRtspCredentials(enc?.url, false)
                  }))
                : [
                    {
                      encoding: item?.customdata ? item?.customdata?.encoding : item?.encoding,

                      resolution: item?.customdata ? item?.customdata?.resolution : item?.resolution,

                      url: item?.customdata?.urlRtsp || item?.urlRtsp || item?.customdata?.url || item?.url //encodeOrDecodeRtspCredentials(item?.customdata?.urlRtsp || item?.urlRtsp || item?.customdata?.url || item?.url, false)
                    }
                  ]
          }))
        }
      ]
    };
    const response = await axiosInstance.post(updateonboardedEndPoint(), requestBody);

    if (response.status === 204 || response.status === 200) {
      console.log("Updated credentials and Rtsp url successfully");
      return {
        success: true,
        message: "Updated credentials and Rtsp url successfully!",
        response: response.data
      };
    } else {
      console.log("Failed to update credentials and Rtsp url");
      return {
        success: false,
        message: "failed to add credentials and Rtsp url"
      };
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to update camera credentials or Rtsp url", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to update camera credentials or Rtsp url, please reload the page.");
    throw error;
  }
};

export const refreshDevices = async (accountId) => {
  try {
    const response = await axiosInstance.post(fetchVisionDevicesEndPoint(), {
      accountname: accountId,
      filter: {
        $sort: [{ field: "createdon", order: "desc" }],
        $expand: "fields,licenses"
      }
    });
    const deviceResponse = response?.data;
    const devices = await segregateDevices(deviceResponse);
    if (devices !== undefined) {
      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.COMPANION_DEVICES_LIST, JSON.stringify(devices?.companionList));
      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.CAMERAS_LIST, JSON.stringify(devices?.cameraList));
      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.FOV_LIST, JSON.stringify(devices?.fovList));
      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.ROI_LIST, JSON.stringify(devices?.roiList));
      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.DEVICE_LIST, JSON.stringify(devices?.deviceList));
      return devices;
    } else {
      console.error("Devices empty");
      return;
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to refresh all devices", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to refresh all devices, please try again.");
    return [];
  }
};

export const fetchAllCompanionDevices = async (activeSession) => {
  try {
    const response = await axiosInstance.post(fetchCompanionDeviceEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $sort: [{ field: "createdon", order: "desc" }],
        $expand: "fieldswithlastupdate"
      }
    });
    const deviceResponse = response?.data ?? [];
    return {
      companiondevices: deviceResponse
    };
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to fetch all companion devices", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to fetch all companion devices, please try again.");
    return [];
  }
};

export const fetchAllCameraAndFovsForRbac = async (activeSession) => {
  try {
    const response = await axiosInstance.post(fetchCameraAndFovsForRbacEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $sort: [{ field: "createdon", order: "desc" }],
        $expand: "fields"
      }
    });
    const deviceResponse = response?.data;
    if (deviceResponse !== undefined) {
      var cameraFovList = [];
      deviceResponse.reverse().forEach((element) => {
        if (element?.fovs != undefined) {
          const hasOfflineStatus =
            element?.fields?.connectivityStatus && element.fields.connectivityStatus.toLowerCase() === DEVICE_CONSTANTS.CAMERA_OFFLINE_STATUS;

          const fovsWithStatus = (element.fovs || []).map((fov) => {
            return hasOfflineStatus ? { ...fov, connectivityStatus: DEVICE_CONSTANTS.CAMERA_OFFLINE_STATUS } : fov;
          });

          cameraFovList.push(...fovsWithStatus);
        }
      });

      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.CAMERAS_LIST, JSON.stringify(deviceResponse));
      sessionStorage.setItem(DEVICE_CONSTANTS.STORAGE_KEYS.FOV_LIST, JSON.stringify(cameraFovList));
      return { cameras: deviceResponse, fovs: cameraFovList };
    } else {
      console.error("Devices empty");
      return {};
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to fetch all cameras and fovs", error);
    throw new Error("Failed to fetch all cameras and fovs, please try again.", errorMessage);
  }
};

export const segregateDevices = async (deviceList) => {
  try {
    let segregatedList = {
      companionList: [],
      cameraList: [],
      fovList: [],
      roiList: [],
      deviceList
    };
    await deviceList?.forEach(async (device) => {
      switch (device.kind) {
        case "ts.device.gateway.vision.companion": {
          segregatedList.companionList.push(device);
          break;
        }
        case "ts.device.onvif": {
          segregatedList.cameraList.push(device);
          break;
        }
        case "ts.device.onvif.fov": {
          segregatedList.fovList.push(device);
          break;
        }
        case "ts.device.onvif.roi": {
          segregatedList.roiList.push(device);
          break;
        }
      }
    });
    return segregatedList;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to segregate devices:", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to segregate devices, please try again.");
    return [];
  }
};

export const refreshCameraDevices = async (activeSession) => {
  try {
    const response = await axiosInstance.post(fetchVisionDevicesEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $selection: {
          kind: "ts.device.onvif"
        },

        $sort: [{ field: "createdon", order: "desc" }],
        $expand: "fields"
      }
    });

    let resData = response.data;
    return resData;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to refresh camera devices:", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to refresh camera devices, please try again.");
    return [];
  }
};

export const refreshCameraDevicesUnderCompanionDevices = async (parentDeviceId, activeSession) => {
  try {
    const response = await axiosInstance.post(fetchVisionDevicesEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $selection: {
          parentdeviceid: parentDeviceId,
          kind: "ts.device.onvif"
        },

        $sort: [{ field: "createdon", order: "desc" }],
        $expand: "fields"
      }
    });

    let resData = response.data;
    return resData;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to refresh camera devices under companion devices:", error);
    // handleApiError("warning", errorMessage ? errorMessage : "Failed to refresh camera devices under companion devices, please try again.");
    return null;
  }
};

export const refreshCompanionDevices = async (activeSession) => {
  try {
    const response = await axiosInstance.post(fetchVisionDevicesEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $selection: {
          kind: "ts.device.gateway.vision.companion"
        },
        $sort: [{ field: "createdon", order: "desc" }],
        $expand: "fields"
      }
    });

    let resData = response.data;
    return resData;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to refresh companion devices:", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to refresh companion devices, please try again.");
    return [];
  }
};

export const getDiscoveredCameras = async ({ companionDeviceList, activeSession, forcediscovery }) => {
  try {
    let tableData = [];
    if (companionDeviceList?.length) {
      await Promise.all(
        companionDeviceList?.map(async (item) => {
          const resourceidentifier = getResourceIdentifierForDevice(item);
          const url = forcediscovery ? forceDiscoveryAPI : fetchDiscoveredCamerasEndPointByCompanion;
          const response = await axiosInstance.post(url(), {
            accountname: activeSession.accountId,
            resourceidentifier: resourceidentifier,
            filter: {
              $sort: [{ field: "createdon", order: "desc" }]
            },
            forcediscovery: forcediscovery
          });
          let resData = response.data;
          if (resData?.length) {
            resData.forEach((cam) => {
              cam.discoveredby = item.id;
              cam.resourceidentifier = resourceidentifier;
              var m = cam.fovs.map((fov) => {
                fov.fovName = "Stream_" + fov.fovID;
                return fov;
              });
              cam.fovs = m;
            });
            tableData = tableData.concat(resData);
          }
        })
      );
      const outputArray = tableData?.map((obj) => ({
        ...obj,
        images: obj.fov?.map((item) => `data:image/png;base64,${item.snapshot}`)
      }));
      return outputArray;
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    throw new Error(errorMessage);
  }
};

export const fetchRegionsOfInterestByTag = async (activeSession, tagId) => {
  try {
    const response = await axiosInstance.post(fetchVisionDevicesEndPoint(), {
      accountname: activeSession.accountId,
      filter: {
        $selection: {
          tagids: tagId,
          kind: "ts.device.onvif.roi"
        },

        $sort: [{ field: "createdon", order: "desc" }]
      }
    });

    let resData = response.data;
    return resData;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to fetch regions of interest:", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to fetch regions of interest, please try again.");
    return [];
  }
};

export const getDiscoveredCamerasForSingleCompanion = async (companionDevice, activeSession) => {
  try {
    const url = forceDiscoveryAPI;
    const response = await axiosInstance.post(url(), {
      accountname: activeSession.accountId,
      resourceidentifier: {
        qrcode: companionDevice
      },
      filter: {
        $sort: [{ field: "createdon", order: "desc" }]
      },
      forcediscovery: true
    });
    let resData = response.data;
    return resData;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to discover cameras:", error);
    handleApiError("warning", errorMessage ? errorMessage : "Failed to discover cameras, please try again.");
    return [];
  }
};

export const getDeviceMappingCollection = (deviceMapping) => {
  const locationDeviceMapping = deviceMapping.map((item) => {
    return item?.deployments?.[0]?.config?.locationDeviceMapping;
  });
  const locationDeviceMappingDeviceId = Object.assign({}, ...locationDeviceMapping);
  return locationDeviceMappingDeviceId;
};

export const getCameraCreds = async (payload, activeSession) => {
  try {
    const url = getCameraCredsEndPoint();

    const requestBody = {
      accountname: activeSession.accountId,
      resourceidentifier: {
        qrcode: payload.qrcode
      }
    };

    const response = await axiosInstance.post(url, requestBody);
    return response.data;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Failed to fetch camera credentials:", error);
    throw new Error(errorMessage || "Failed to fetch camera credentials");
  }
};
